export type ReviewItem = {
  endpoint?: "A" | "B";
  name: string;
  active?: boolean;
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ReviewSnapshot = {
  viewport: { width: number; height: number };
  targets: ReviewItem[];
  labels: ReviewItem[];
  tutorialCard: { left: number; top: number; right: number; bottom: number } | null;
};

const MARGIN = 16;

/** A portal choice smaller than this on either screen axis is unreadable. */
export const MIN_TARGET_FOOTPRINT_PX = 28;

/**
 * Screen-space region reserved for the tutorial coach card (top-left).
 * Single source of truth: the Tutorial DOM positions its card from this rect,
 * and the editor/unit fixtures reserve the same region, so the value cannot
 * drift between the game, the editor review, and the QA suites.
 */
export const TUTORIAL_CARD_RECT = { left: 18, top: 18, right: 388, bottom: 142 } as const;

export function arenaReadabilityIssues(snapshot: ReviewSnapshot): string[] {
  const issues: string[] = [];
  const active = snapshot.targets.filter((target) => target.active);
  const reviewable = active.filter((target) => insideViewport(target, snapshot.viewport));
  for (const endpoint of ["A", "B"] as const) if (!reviewable.some((target) => target.endpoint === endpoint)) issues.push(`no fully visible ${endpoint} target`);
  for (const target of reviewable) {
    if (Math.min(target.width, target.height) < MIN_TARGET_FOOTPRINT_PX) issues.push(`${target.endpoint}:${target.name} is too small to read`);
    if (!snapshot.labels.some((label) => label.name === target.name)) issues.push(`${target.endpoint}:${target.name} has no visible identifier`);
    if (snapshot.tutorialCard && overlaps(target, snapshot.tutorialCard)) issues.push(`${target.endpoint}:${target.name} is covered by tutorial UI`);
  }
  for (const label of snapshot.labels) if (!insideViewport(label, snapshot.viewport)) issues.push(`${label.name} label clips viewport`);
  return issues;
}

function insideViewport(item: ReviewItem, viewport: ReviewSnapshot["viewport"]): boolean {
  return item.x - item.width / 2 >= MARGIN && item.x + item.width / 2 <= viewport.width - MARGIN && item.y - item.height / 2 >= MARGIN && item.y + item.height / 2 <= viewport.height - MARGIN;
}
function overlaps(item: ReviewItem, rect: NonNullable<ReviewSnapshot["tutorialCard"]>): boolean {
  return item.x + item.width / 2 >= rect.left && item.x - item.width / 2 <= rect.right && item.y + item.height / 2 >= rect.top && item.y - item.height / 2 <= rect.bottom;
}
